// client/src/components/auth/profile/TennisProfileCard.js
import React, { useState, useEffect } from 'react'
import FloatingSelect from './FloatingSelect'
import FloatingInput from './FloatingInput'
import addRipple from '../../../utils/ripple'
import '../../../styles/profileFields.css'

function formatDateForInput(dateString) {
  if (!dateString) return ''
  // Handle both ISO date strings and YYYY-MM-DD format
  if (typeof dateString === 'string' && dateString.includes('T')) {
    // It's an ISO datetime string, extract just the date part
    return dateString.slice(0, 10)
  }
  if (
    typeof dateString === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(dateString)
  ) {
    // It's already in YYYY-MM-DD format
    return dateString
  }
  // Try to parse as date and format, but avoid timezone issues
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function TennisProfileCard({
  playerData,
  onPlayerDataChange,
  isEditing,
  onEditClick,
  editingField,
  onInlineSave,
  onInlineCancel,
}) {
  const [formData, setFormData] = useState({
    skill_level: playerData?.skill_level || null,
    gender: playerData?.gender || '',
    birthdate: formatDateForInput(playerData?.birthdate) || '',
  })
  const [showSkillLevelTooltip, setShowSkillLevelTooltip] = useState(false)

  // Update form data when playerData changes or isEditing changes
  useEffect(() => {
    if (playerData) {
      setFormData({
        skill_level: playerData.skill_level || null,
        gender: playerData.gender || '',
        birthdate: formatDateForInput(playerData.birthdate) || '',
      })
    } else {
      // If playerData is null (e.g. new player or error), reset form
      setFormData({ skill_level: null, gender: '', birthdate: '' })
    }
  }, [playerData, isEditing]) // formData resets if isEditing changes (e.g. on cancel from parent)

  const handleInputChange = (e) => {
    const { name, value } = e.target
    let processedValue = value
    // birthdate is stored as string; no special processing needed
    const newLocalFormData = { ...formData, [name]: processedValue }
    setFormData(newLocalFormData)
    onPlayerDataChange(newLocalFormData) // Call onPlayerDataChange after local state update
  }

  const playTypeOptions = [
    { value: '', label: '-- Select Type --' },
    { value: 'Men', label: "Men's Leagues" },
    { value: 'Women', label: "Women's Leagues" },
  ]

  const getSkillLevelValue = () => {
    if (formData?.skill_level !== undefined && formData?.skill_level !== null) {
      return Number(formData?.skill_level).toFixed(1)
    }
    return ''
  }

  const isFieldEditing = (field) => isEditing && editingField === field

  // Render field chip - don't define as component to avoid remounting
  const renderFieldChip = (field, label, value, editor) => {
    if (isFieldEditing(field)) {
      return (
        <div className="field-chip editing" data-field={field} key={field}>
          <span className="chip-label">{label}</span>
          <div className="chip-editor">
            {editor}
            <div className="chip-actions">
              <button
                type="button"
                className="icon-btn confirm"
                onClick={() => onInlineSave && onInlineSave(field)}
                aria-label="Save"
              >
                ✓
              </button>
              <button
                type="button"
                className="icon-btn cancel"
                onClick={() => onInlineCancel && onInlineCancel(field)}
                aria-label="Cancel"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )
    }
    return (
      <button
        type="button"
        className="field-chip"
        key={field}
        onClick={(e) => {
          addRipple(e)
          onEditClick(field)
        }}
      >
        <span className="chip-label">{label}</span>
        <span className="chip-value">{value || '—'}</span>
      </button>
    )
  }

  return (
    <div className="profile-card">
      <h5 className="section-title">🎾 Tennis Profile</h5>
      <div className="chip-row">
        <div className="field-chip read-only" style={{ position: 'relative' }}>
          <span className="chip-label">
            Skill Level
            <span
              className="info-icon ms-1"
              onMouseEnter={() => setShowSkillLevelTooltip(true)}
              onMouseLeave={() => setShowSkillLevelTooltip(false)}
              onClick={() => setShowSkillLevelTooltip(!showSkillLevelTooltip)}
            >
              ⓘ
            </span>
          </span>
          <span className="chip-value">{getSkillLevelValue() || '—'}</span>
          {showSkillLevelTooltip && (
            <div className="skill-level-tooltip">
              Skill level is set during league registration
            </div>
          )}
        </div>
        {renderFieldChip(
          'gender',
          'League Category',
          formData?.gender || '',
          <FloatingSelect
            label="League Category"
            name="gender"
            value={formData?.gender || ''}
            onChange={handleInputChange}
            options={playTypeOptions}
            disabled={!isFieldEditing('gender')}
          />,
        )}
        {renderFieldChip(
          'birthdate',
          'Birthdate',
          formData?.birthdate || '',
          <FloatingInput
            label="Birthdate"
            name="birthdate"
            type="date"
            value={formData?.birthdate || ''}
            onChange={handleInputChange}
            disabled={!isFieldEditing('birthdate')}
          />,
        )}
      </div>
    </div>
  )
}

export default TennisProfileCard
